import React, { createContext, useContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, ClipboardList, Compass, Handshake } from "lucide-react";

const MobileNavContext = createContext({ hidden: false, setHidden: () => {} });
export const MobileNavProvider = ({ children }) => {
  const [hidden, setHidden] = useState(false);
  return <MobileNavContext.Provider value={{ hidden, setHidden }}>{children}</MobileNavContext.Provider>;
};
export const useMobileNav = () => useContext(MobileNavContext);

const TABS = [
  { to: "/", label: "Home", Icon: Home, match: (p) => p === "/" },
  { to: "/assessments", label: "Assessments", full: "Explore Assessments", Icon: ClipboardList, match: (p) => p === "/assessments" },
  { to: "/discover", label: "Discover Free", Icon: Compass, hero: true, match: (p) => p.startsWith("/discover") },
  { to: "/partner", label: "Partner", full: "Partner with Us", Icon: Handshake, match: (p) => p === "/partner" },
];

export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const { hidden } = useMobileNav();
  const visible = !pathname.startsWith("/masteradmin") && !hidden;

  useEffect(() => {
    if (visible) document.body.classList.add("has-mobile-nav");
    else document.body.classList.remove("has-mobile-nav");
    return () => document.body.classList.remove("has-mobile-nav");
  }, [visible]);

  if (!visible) return null;

  return (
    <nav
      aria-label="Primary mobile navigation"
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed inset-x-0 bottom-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-6px_24px_-12px_rgba(15,23,42,0.25)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <ul className="flex items-stretch">
        {TABS.map((t) => {
          const active = t.match(pathname);
          return (
            <li key={t.to} className="flex-1">
              <Link
                to={t.to}
                aria-current={active ? "page" : undefined}
                data-testid={`mnav-${t.to === "/" ? "home" : t.to.replace("/", "")}`}
                className="relative flex flex-col items-center justify-center gap-1 min-h-[58px] px-1 pt-2 pb-1.5 transition-colors"
              >
                {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 h-[3px] w-9 rounded-full cta-gradient" />}
                <span
                  className={`grid h-8 w-8 place-items-center rounded-xl transition-all ${
                    active
                      ? "cta-gradient text-white shadow-md shadow-violet-300/50"
                      : t.hero
                      ? "bg-teal-50 text-teal-600"
                      : "text-slate-400"
                  }`}
                >
                  <t.Icon className="h-[18px] w-[18px]" strokeWidth={active ? 2.4 : 2} />
                </span>
                <span
                  className={`text-[11px] font-bold leading-none tracking-tight ${
                    active ? "text-violet-700" : t.hero ? "text-teal-600" : "text-slate-500"
                  }`}
                >
                  {t.label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
